import React, { useMemo, FC, useEffect, useState } from "react";
import {
  useTable,
  useSortBy,
  usePagination,
  useRowSelect,
  useExpanded,
  Column,
  Cell,
} from "react-table";

import Svg from "components/common/SVG/Svg";
import { useAppSelector } from "core/helpers";
import DataTablePagination from "./DataTablePagination";
import SectionLoader from "../AppLoader/SectionLoader";

type TProps = {
  tableColumns: readonly Column<object>[];
  tableData: readonly object[];
  isLoading?: boolean;
  tableClassName?: string;
  // Required fields for pagination and sorting
  setOrderBy?: (orderBy: string | null) => void;
  setIsDesc?: (desc: boolean | null) => void;
  hasPagination?: boolean;
  recordsCount?: number;
  pageSize?: number;
  setPageNumber?: (num: number) => void;
  classContainer?: string;
  hiddenColumns?: string[];
  onSort?: (key: string) => void;
  modalListChildren?: () => React.ReactNode;
  renderRowSubComponent?: (row: any) => React.ReactNode;
  handleRowClick?: (row: any) => void;
  expanded?: Record<string, boolean>;
  preventLoader?: boolean;
  onExpandedChange?: (expanded: Record<string, boolean>) => void;
  processCell?: (row: any, cellProps: Cell<object, any>) => any;
  minTableWidth?: string;
};

const DataTable: FC<TProps> = ({
  tableColumns,
  tableData,
  tableClassName,
  hasPagination,
  pageSize,
  recordsCount,
  setPageNumber,
  setOrderBy,
  setIsDesc,
  classContainer,
  hiddenColumns,
  onSort,
  modalListChildren,
  renderRowSubComponent,
  handleRowClick,
  expanded,
  onExpandedChange,
  preventLoader,
  processCell,
  minTableWidth,
}) => {
  const rowData = useMemo(() => tableData, [tableData]);
  const appState = useAppSelector(state => state.app);
  const showLoader = appState.showLoader && !preventLoader;

  const [order, setOrder] = useState<boolean>(true);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    columns,
    visibleColumns,
    state: { sortBy },
  } = useTable(
    {
      columns: tableColumns,
      data: rowData,
      initialState: {
        hiddenColumns: hiddenColumns || [],
      },
      expanded: expanded || {},
      onExpandedChange,
    },
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
  );

  useEffect(() => {
    if (setOrderBy) setOrderBy(sortBy.length !== 0 ? sortBy[0].id : null);
    if (setIsDesc) setIsDesc(sortBy.length !== 0 ? !!sortBy[0].desc : null);
  }, [sortBy]);

  const handleSortingChange = (accessor: string) => {
    if (onSort) {
      setOrder(!order);
      onSort(`${accessor} ${order ? "desc" : "asc"}`);
    }
  };

  return (
    <>
      {/* className="table-responsive" */}
      <div className={`${classContainer ?? "table-responsive-md"}`}>
        <table
          className={`table ${tableClassName || ""}`}
          {...getTableProps()}
          style={{ minWidth: minTableWidth }}
        >
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: any) => (
                  <th
                    colSpan={columns.length}
                    className={column?.className || "text-center"}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    onClick={() => !column.disableSortBy && handleSortingChange(column.id)}
                  >
                    <div
                      className={`d-flex cell align-items-center text-primary ${
                        column.className === "text-end"
                          ? "justify-content-end"
                          : "justify-content-center"
                      }`}
                    >
                      {column.render("Header")}
                      <div
                        className={`${
                          column.disableSortBy ? "d-none" : "d-flex"
                        } flex-column table-sorting ms-1`}
                        style={{ marginTop: "1px" }}
                      >
                        <button
                          type="button"
                          className={`d-flex btn btn-transparent p-0
                                                            ${
                                                              column.isSorted &&
                                                              !column.isSortedDesc
                                                                ? "active"
                                                                : ""
                                                            }
                                                        `}
                        >
                          <Svg icon="arrowUp" width={9} height={6} />
                        </button>
                        <button
                          type="button"
                          className={`d-flex btn btn-transparent p-0
                                                            ${
                                                              column.isSorted && column.isSortedDesc
                                                                ? "active"
                                                                : ""
                                                            }
                                                            `}
                        >
                          <Svg icon="arrowDown" width={9} height={6} />
                        </button>
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {showLoader && (
              <div
                className="row text-center align-items-center justify-content-center w-100 position-absolute"
                style={{ height: "30vh" }}
              >
                <SectionLoader />
              </div>
            )}
            {!showLoader && rows.length !== 0
              ? rows.map(row => {
                  prepareRow(row);
                  const rowProps = row.getRowProps();
                  return (
                    <React.Fragment key={rowProps.key}>
                      <tr
                        className="text-center align-middle"
                        {...rowProps}
                        onClick={() => handleRowClick && handleRowClick(row)}
                      >
                        {row.cells.map(cell => {
                          const processedCell = processCell && processCell(row, cell);
                          const cellProps = processedCell?.props || cell.getCellProps();
                          return (
                            !processedCell?.isHidden && (
                              <td {...cellProps} className={(cell.column as any)?.className || ""}>
                                <span>{cell.render("Cell")}</span>
                              </td>
                            )
                          );
                        })}
                      </tr>
                      {row.isExpanded ? (
                        <tr>
                          <td colSpan={visibleColumns.length}>
                            {renderRowSubComponent && renderRowSubComponent({ row })}
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })
              : !showLoader && (
                  <tr>
                    <td className="text-center" colSpan={columns.length}>
                      No data found.
                    </td>
                  </tr>
                )}
          </tbody>
        </table>
      </div>
      {modalListChildren && modalListChildren()}
      {hasPagination && pageSize && recordsCount && setPageNumber && (
        <DataTablePagination
          pageSize={pageSize}
          recordsCount={recordsCount}
          setPageNumber={setPageNumber}
        />
      )}
    </>
  );
};

export default DataTable;
